import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { DataGridPremium, GridCellEditStopParams, useGridApiRef } from '@mui/x-data-grid-premium';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import { buttonVariants, IconButton } from '@clatter/ui';
import { useDeepCompareMemo } from '@clatter/platform';
import { augmentEntityWithClientName } from '../../helpers';
import { SearchInput, IconButtonRounded, NgButton } from '../UI';

interface QueryOutputTableProps {
  rows: any[];
  entityPrimaryKey: string;
  onChange: (data: any) => void;
  isLoading: boolean;
  schema?: any;
}

const StyledErrorMessages = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  h4 {
    font-weight: bold;
    color: ${({ theme }) => theme.palette.error};
  }

  li {
    padding: 1rem 2rem;
    background-color: ${({ theme }) => theme.palette.errorDimmed};
    margin-bottom: 0.3rem;

    p {
      margin: 0;
    }
  }
`;

const StyledDataGridWrapper = styled.div`
  position: relative;
  width: 100%;

  .header {
    display: flex;
    justify-content: flex-start;
    gap: ${({ theme }) => theme.spacing.small};

    margin-bottom: ${({ theme }) => theme.spacing.small};

    #edit-input-wrapper {
      flex: 1;
      padding-right: ${({ theme }) => theme.spacing.medium};
    }
  }

  .MuiDataGrid-root {
    min-height: 150px;
  }

  .MuiTablePagination-root {
    p {
      margin-bottom: 0;
    }
  }

  cursor: default;

  .MuiDataGrid-cell--editable {
    background: rgb(255, 249, 230);

    &:hover {
      cursor: url('../../../../../../assets/icons/edit_icon.svg') 0 16, auto;
    }
  }
`;

const StyledTextInputContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  align-items: center;
  box-sizing: border-box;
  padding: 0;

  .actions {
    display: flex;
  }

  .input-container {
    border-color: ${({ theme }) => theme.palette.textDimmed};
  }

  &.cell-input {
    .input-container {
      border: none;
      background: none;

      .error {
        border: 2px solid red;
      }
    }
  }
`;

const CustomEditComponent = ({ id, field, value, api, handleRowEdit }: any) => {
  const [editValue, setEditValue] = useState(value);

  const hasError = useMemo(() => !editValue?.trim(), [editValue]);

  const isSaveDisabled = useMemo(() => editValue === value || hasError, [editValue, value, hasError]);

  const handleSave = (e: any) => {
    e.stopPropagation();

    api?.stopCellEditMode({ id, field });
    handleRowEdit({ id, field, value: editValue });
  };

  const handleCancel = (e: any) => {
    e.stopPropagation();
    api?.stopCellEditMode({ id, field });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave(e);
    }
  };

  const inputTarget = document.getElementById('edit-input-wrapper');
  const placeholderText = `Enter ${field}...`;

  return (
    <>
      <StyledTextInputContainer className="cell-input">
        <SearchInput
          value={editValue}
          placeholder={placeholderText}
          onSearch={setEditValue}
          onKeyDown={handleKeyDown}
          hasError={hasError}
          shouldFocus
          noDebounce
          hideClear
          hideIcon
        />

        <div className="actions">
          <IconButtonRounded onClick={handleSave} icon={<CheckIcon />} disabled={isSaveDisabled} title="Accept" />
          <IconButtonRounded onClick={handleCancel} icon={<CloseIcon />} title="Cancel" />
        </div>
      </StyledTextInputContainer>

      {inputTarget &&
        ReactDOM.createPortal(
          <StyledTextInputContainer>
            <div className="actions">
              <IconButtonRounded onClick={handleSave} icon={<CheckIcon />} disabled={isSaveDisabled} title="Accept" />
              <IconButtonRounded onClick={handleCancel} icon={<CloseIcon />} title="Cancel" />
            </div>

            <SearchInput
              value={editValue}
              placeholder={placeholderText}
              onSearch={setEditValue}
              onKeyDown={handleKeyDown}
              hasError={hasError}
              noDebounce
              hideClear
              hideIcon
            />
          </StyledTextInputContainer>,
          inputTarget,
        )}
    </>
  );
};

export const QueryOutputTable: FC<QueryOutputTableProps> = ({
  rows: initialRows,
  entityPrimaryKey,
  schema,
  isLoading,
  onChange,
}) => {
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const apiRef = useGridApiRef();

  const handleCellClick = (params: any) => {
    if (params.colDef.editable) {
      apiRef?.current?.startCellEditMode({ id: params.id, field: params.field });
      return;
    }
  };

  const discardChanges = () => {
    const reverted = initialRows.map((item: any) => augmentEntityWithClientName(item, entityPrimaryKey, true));
    onChange(reverted);
    setIsDirty(false);
  };

  const handleRowEdit = (data: any, params: GridCellEditStopParams) => {
    const updatedRow = { ...params.row, [params.field]: data.value };

    const index = initialRows.findIndex((item: any) => item[entityPrimaryKey] === updatedRow[entityPrimaryKey]);
    if (index === -1) {
      console.warn(`Entity not found in selected list primary key ${updatedRow[entityPrimaryKey]}`);
      return;
    }

    const updated = [...initialRows];
    updated[index] = updatedRow;

    // setValue('selectedEntities', updated);
    onChange(updated);
    setIsDirty(true);
  };

  const handleRemove = useCallback((row: any ) => {
    const updated = initialRows.filter((item: any) => item[entityPrimaryKey] !== row[entityPrimaryKey]);
    onChange(updated);
  }, [initialRows, entityPrimaryKey, onChange])

  const columns = useDeepCompareMemo(() => {
    if (!schema) {
      return [];
    }

    const schemaColumns = Object.keys(schema?.items?.properties || {}).map((key) => {
      const columnData: any = {
        field: key,
        headerName: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        flex: key === entityPrimaryKey ? 0.3 : 1, //@todo - hardcoded key
        minWidth: 90,
      };

      // set editable columns
      if (key === 'CLIENT_DISPLAY_NAME') {
        //@todo this should be dynamic based on schema?
        columnData['editable'] = true;
      }

      return columnData;
    });

    return [
      ...schemaColumns,
      {
        field: 'remove',
        headerName: 'Remove',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        disableExport: true,
        renderCell: (params: any) => {
          return (
            //@ts-ignore
            <IconButton data-id={params.row.id} onClick={() => handleRemove(params.row)} tooltip="Remove">
              <CloseIcon />
            </IconButton>
          );
        },
      },
    ];
  }, [schema, initialRows, entityPrimaryKey]);

  const mappedColumns = useDeepCompareMemo(() => {
    return (columns || []).map((column: any) => {
      if (column?.editable) {
        return {
          renderEditCell: (params: any) => (
            <CustomEditComponent
              id={params.id}
              field={params.field}
              value={params.value}
              api={params.api}
              apiRef={apiRef}
              handleRowEdit={(data: any) => handleRowEdit(data, params)}
            />
          ),
          ...column,
          headerName: `${column.headerName} (EDITABLE)`,
          flex: 1,
        };
      }
      return column;
    });
  }, [columns, apiRef, handleRowEdit]);

  return (
    <StyledDataGridWrapper>
      <div className="header">
        <div id="edit-input-wrapper"></div>

        <NgButton onClick={discardChanges} variant={buttonVariants.clear} disabled={!isDirty}>
          Discard Changes
        </NgButton>

        <NgButton
          disabled={initialRows?.length <= 0}
          variant={buttonVariants.clear}
          testId="data-page-selected-remove-all-btn"
          onClick={() => onChange([])}
        >
          Remove All
        </NgButton>
      </div>
      <DataGridPremium
        apiRef={apiRef}
        columns={mappedColumns}
        rows={initialRows}
        onCellClick={handleCellClick}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        rowHeight={30}
        columnHeaderHeight={30}
        pagination
        autoHeight={true}
        loading={isLoading}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 5 },
          },
        }}
        sx={{
          '& .MuiDataGrid-footerContainer': {
            height: '40px',
            minHeight: '40px',
          },
        }}
      />
    </StyledDataGridWrapper>
  );
};
